import React, { useState } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    InputAdornment,
    IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, School } from '@mui/icons-material';
import { loginWithLocalAccount, setActiveSchoolId } from '../services/dataService';

const Login = ({ onLogin }) => {
    const [credentials, setCredentials] = useState({
        loginId: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const user = await loginWithLocalAccount({
                loginId: credentials.loginId,
                password: credentials.password,
            });
            if (user?.schoolId) {
                setActiveSchoolId(user.schoolId);
            }
            onLogin({
                ...user,
                id: user.id || user.loginId,
                name: user.name,
                role: user.role,
                loginType: 'local'
            });
        } catch (error) {
            if (error?.code === 'session_not_established') {
                setError('このブラウザではログイン状態を保持できません。通常のSafariまたはChromeで開き直してください。');
                return;
            }
            const status = error?.response?.status;
            if (status === 401) {
                setError('ログインIDまたはパスワードが正しくありません。');
            } else if (status === 403) {
                setError('このアカウントは現在無効です。管理者に連絡してください。');
            } else {
                setError('ログインに失敗しました。時間をおいて再度お試しください。');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setCredentials({
            ...credentials,
            [e.target.name]: e.target.value
        });
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <School sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography component="h1" variant="h4" gutterBottom>
                            生徒管理システム
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            ログインしてシステムにアクセス
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="username"
                            label="ログインID"
                            name="loginId"
                            autoComplete="username"
                            autoFocus
                            value={credentials.loginId}
                            onChange={handleChange}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="パスワード"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={credentials.password}
                            onChange={handleChange}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="パスワードを表示"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={loading || !credentials.loginId || !credentials.password}
                        >
                            {loading ? 'ログイン中...' : 'ログイン'}
                        </Button>
                    </Box>

                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            セキュリティ強化のため、初回管理者アカウントは
                            環境変数で安全に設定し、ログイン後に必ずパスワード変更を行ってください。
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default Login; 
